<script lang="ts" module>
	const riskFactors = [
		'tobacco',
		'alcohol',
		'ultra_processed_foods',
		'air_pollution',
		'physical_inactivity'
	] as const;

	const regions = {
		head_neck: { x: '50%', y: '15%' },
		respiratory_system: { x: '50%', y: '35%' },
		digestive_system: { x: '50%', y: '55%' },
		urinary_system: { x: '40%', y: '65%' },
		reproductive_system: { x: '50%', y: '70%' },
		breast: { x: '63%', y: '43%' }
	};
</script>

<script lang="ts">
	import { fade, scale, draw } from 'svelte/transition';
	import { getCopy } from '$lib/lang/copy.svelte';
	import { RadioGroup } from 'bits-ui';
	import { curveBumpX, line } from 'd3';

	import Picture from '$lib/components/media/Picture.svelte';
	import Divider from '$lib/components/fragments/Divider.svelte';

	const copy = $derived(getCopy().risk_factors.chart);

	let selectedRiskFactor = $state<(typeof riskFactors)[number]>(riskFactors[0]);
	let selectedData = $derived(copy?.data?.[selectedRiskFactor]);

	// Element references
	let containerRef = $state<HTMLElement>();
	let containerDims = $state({ width: 0, height: 0 });

	let radioButtonRefs = $state<Record<(typeof riskFactors)[number], HTMLElement>>({});
	let regionRefs = $state<Record<keyof typeof regions, HTMLElement>>({});
	let regionWidths = $state<Record<keyof typeof regions, number>>({});

	// Create line generator for curved paths
	const lineGenerator = line().curve(curveBumpX);

	// Function to get element position relative to container
	function getElementPosition(element: HTMLElement, container: HTMLElement, end = false) {
		const elementRect = element.getBoundingClientRect();
		const containerRect = container.getBoundingClientRect();

		return {
			x: elementRect.left - containerRect.left + (end ? elementRect.width : 0),
			y: elementRect.top - containerRect.top + elementRect.height / 2
		};
	}

	// Dynamic line paths, reactive to element and container resize
	let linePaths = $derived.by(() => {
		if (!selectedData || !containerRef || !containerDims.width || !containerDims.height) return [];

		const selectedButton = radioButtonRefs[selectedRiskFactor];
		if (!selectedButton) return [];

		const buttonPos = getElementPosition(selectedButton, containerRef, true);

		return selectedData.related_regions
			.map((regionKey: keyof typeof regions) => {
				const regionElement = regionRefs[regionKey];
				if (!regionElement || !containerRef || !regionWidths[regionKey]) return undefined;

				const regionPos = getElementPosition(regionElement, containerRef);

				// Create curved path from button to region
				const pathData = lineGenerator([
					[buttonPos.x, buttonPos.y],
					[regionPos.x, regionPos.y]
				]);

				return { key: regionKey, path: pathData };
			})
			.filter(Boolean);
	});
</script>

<div class="content-well-lg">
	<div class="border-gray-dark flex flex-col items-center gap-4 border-y py-6">
		<h2 class="heading-4 text-yellow text-center">
			{copy.title}
		</h2>

		<p class="subtitle-2 flex items-center gap-2 text-white">
			<iconify-icon icon="ic:baseline-ads-click" class="text-lg"></iconify-icon>
			{copy.subtitle}
		</p>
	</div>

	<div
		class="border-gray-dark custom-grid relative border-b"
		bind:clientWidth={containerDims.width}
		bind:clientHeight={containerDims.height}
		bind:this={containerRef}
	>
		<!-- Background lines dynamic to selection -->
		<svg class="pointer-events-none absolute inset-0 h-full w-full">
			{#each linePaths as { key, path } (key)}
				<path
					d={path}
					stroke="white"
					stroke-width="1"
					stroke-dasharray="2,2"
					fill="none"
					in:draw
					out:fade
				/>
			{/each}
		</svg>

		<!-- Risk Factors Selection -->
		<div class="relative mt-8 mb-auto flex max-w-[270px] shrink-0 flex-col items-center gap-4">
			<p class="over-line-2 text-white">Risk Factors</p>
			<RadioGroup.Root class="flex w-full flex-col gap-3" bind:value={selectedRiskFactor}>
				{#each riskFactors as value (value)}
					{@const label = copy.data[value].title}
					<div bind:this={radioButtonRefs[value]}>
						<RadioGroup.Item
							class="button data-[state=checked]:bg-yellow data-[state=checked]:text-blue-dark w-full cursor-pointer rounded-full border border-white px-3 py-2 leading-tight text-white transition-all hover:scale-102 md:px-6 md:py-3"
							{value}
						>
							{label}
						</RadioGroup.Item>
					</div>
				{/each}
			</RadioGroup.Root>
		</div>

		<!-- Body Diagram -->
		<div class="relative mt-auto flex min-h-[550px] pt-4">
			<Picture src="body.webp" alt={copy.alt} class="h-full w-full object-cover" />
			{#each Object.entries(regions) as [region_key, position] (region_key)}
				{@const active = selectedData.related_regions.includes(region_key)}
				<div
					class={[
						'absolute flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full text-center font-serif text-[10px] italic transition-all duration-500 md:text-xs',
						active
							? 'bg-blue-light/60 shadow-custom h-16 w-16 p-1 md:h-20 md:w-20'
							: 'bg-gray h-4 w-4'
					]}
					style:left={position.x}
					style:top={position.y}
					bind:this={regionRefs[region_key]}
					bind:clientWidth={regionWidths[region_key]}
				>
					{#if active}
						<span transition:scale>
							{copy.regions[region_key]}
						</span>
					{/if}
				</div>
			{/each}
		</div>

		<!-- Details Panel -->
		<div
			class="bg-gray z-10 col-span-2 -mt-26 mb-4 flex max-h-[350px] shrink-0 flex-col gap-4 overflow-auto p-6 text-black md:col-span-1 md:mt-8 md:mb-auto md:max-h-[484px]"
		>
			<div class="flex items-center gap-3">
				<p class="heading-5">{selectedData?.title}</p>
			</div>

			<Divider dashed={false} />

			<p class="body-2">{selectedData?.description}</p>

			<a href={selectedData?.source} target="_blank" class="body-2 underline"
				>{copy.research}

				<iconify-icon icon="lsicon:open-new-filled" class="inline-block h-4 w-4"></iconify-icon>
			</a>

			<p class="subtitle-2 bg-yellow w-fit px-2 py-1">{copy.related}</p>

			<div>
				{#each selectedData.related_cancers as cancer (cancer)}
					<div
						class="border-gray-dark subtitle-2 text-blue flex items-center justify-between border-b py-2"
					>
						{cancer}
					</div>
				{/each}
			</div>
		</div>
	</div>
</div>

<style lang="postcss">
	@reference '$lib/styles/app.css';

	.shadow-custom {
		box-shadow: 0 0 10px 1px var(--color-white);
	}

	.custom-grid {
		@apply grid grid-cols-2 items-center border-b md:grid-cols-3 md:grid-rows-1;

		grid-template-rows: repeat(2, auto);
	}
</style>
